import { PropsWithChildren, ReactElement } from "react";
import { ReactComponent as AiIcon } from "src/assets/icons-redesign/ai-star.svg";
import { ReactComponent as HumanIcon } from "src/assets/icons-redesign/human-group.svg";
import { convertToAttributes, generateOptionsString } from "src/components/criteria/criteria-settings";
import { Skeleton } from "src/components/ui/skeleton";
import { SurveyAudienceCriteria } from "src/redux/survey/survey.types";
import ResultsSidebarDetailsTooltip from "./results-sidebar-details-tooltip";

type DetailsTitleProps =
  | {
    variant: "skeleton",
    skeletonTitle?: string,
    skeletonCount?: boolean,
  }
  | {
    variant: "title",
    title: string,
    value?: number | string,
  };

const DetailsTitle = (props: DetailsTitleProps): ReactElement => {
  const { variant } = props;

  if (variant === "skeleton") {
    const { skeletonTitle, skeletonCount } = props;

    const renderSkeletonTitle = () => {
      if (!skeletonTitle) {
        return <Skeleton className={`h-6 ${skeletonCount ? "w-2/3" : "w-full"}`} />;
      }

      return (
        <span>
          {skeletonTitle}
        </span>
      );
    };

    return (
      <div className="flex justify-between text-base font-medium">
        {renderSkeletonTitle()}
        {skeletonCount && <Skeleton className="h-6 w-8" />}
      </div>
    );
  }

  const { title, value } = props;

  return (
    <div className="flex justify-between text-base font-medium">
      <span>
        {title}
      </span>
      {value !== undefined && (
        <span>
          {value}
        </span>
      )}
    </div>
  );
};


const DetailsItem = ({ title, titleIcon, value, tooltip }: { title: string, titleIcon?: ReactElement, value: string, tooltip?: string }): ReactElement => {
  return (
    <div className="flex justify-between text-base gap-2">
      <div className="flex gap-2 items-center">
        {Boolean(titleIcon) && titleIcon}
        {title}
      </div>
      <div className="flex gap-1 text-right items-center">
        {value}
        <ResultsSidebarDetailsTooltip tooltip={tooltip} />
      </div>
    </div>
  );
};

const DetailsLayout = ({ children }: PropsWithChildren): ReactElement => {
  return (
    <div className="flex flex-col gap-2">
      {children}
    </div>
  );
};

type RespondentsDetailsProps = {
  // 4/21/25: aiUsers and humanUsers converted (BE) to more detailed object structure w/ started vs completed info
  // 'any' types obviously not ideal, but left for now to avoid breaking changes to all other survey stats parsing logic
  aiUsers?: any,
  humanUsers?: any,
  totalUsers: number,
  alignmentScore?: number,
};

const ResultsSidebarDetailsRespondents = (props: RespondentsDetailsProps): ReactElement => {
  const { aiUsers, alignmentScore, humanUsers, totalUsers } = props;

  return (
    <DetailsLayout>
      <DetailsTitle
        variant="title"
        title="Respondents"
        value={totalUsers}
      />
      {humanUsers !== undefined && (
        <DetailsItem
          title="Human"
          titleIcon={(
            <div className="flex justify-center items-center text-white size-6 rounded-lg bg-black">
              <HumanIcon className="size-3.5" />
            </div>
          )}
          value={String(humanUsers.started)}
        />
      )}
      {aiUsers !== undefined && (
        <DetailsItem
          title="AI Panelist"
          titleIcon={(
            <div className="flex justify-center items-center text-white size-6 rounded-lg bg-purple-600">
              <AiIcon className="size-3.5" />
            </div>
          )}
          value={String(aiUsers.started)}
        />
      )}
      <hr className="my-2" />
      <DetailsTitle
        variant="title"
        title="Completion Rate"
        value={(aiUsers?.started + humanUsers?.started) === 0 ? `(0)` : `${(((aiUsers?.completed + humanUsers?.completed) / (aiUsers?.started + humanUsers?.started)) * 100).toFixed(1).replace('.0', '')}% (${aiUsers?.completed + humanUsers?.completed})`}
      />
      {humanUsers !== undefined && (
        <DetailsItem
          title="Human"
          titleIcon={(
            <div className="flex justify-center items-center text-white size-6 rounded-lg bg-black">
              <HumanIcon className="size-3.5" />
            </div>
          )}
          value={humanUsers.started === 0 ? `(0)` : `${((humanUsers.completed / humanUsers.started) * 100).toFixed(1).replace('.0', '')}% (${humanUsers.completed})`}
        />
      )}
      {aiUsers !== undefined && (
        <DetailsItem
          title="AI Panelist"
          titleIcon={(
            <div className="flex justify-center items-center text-white size-6 rounded-lg bg-purple-600">
              <AiIcon className="size-3.5" />
            </div>
          )}
          value={aiUsers.started === 0 ? `(0)` : `${((aiUsers.completed / aiUsers.started) * 100).toFixed(1).replace('.0', '')}% (${aiUsers.completed})`}
        />
      )}
      {alignmentScore !== undefined && (
        <>
          <hr className="my-2" />
          <DetailsItem
            title="Alignment"
            value={`${+alignmentScore.toFixed(1)}%`}
            tooltip="This score reflects the alignment percentage between AI panelists and human survey respondents."
          />
        </>
      )}
    </DetailsLayout>
  );
};

type ResultsSidebarDetailsAudienceProps = {
  criteria?: SurveyAudienceCriteria;
};

const ResultsSidebarDetailsAudience = ({ criteria }: ResultsSidebarDetailsAudienceProps): ReactElement | null => {
  if (!criteria) {
    return null;
  }

  return (
    <DetailsLayout>
      <DetailsTitle
        variant="title"
        title="Target Audience"
      />
      {convertToAttributes(criteria).map(crit => (
        <DetailsItem
          key={crit.value}
          title={crit.label}
          value={generateOptionsString(crit.options, crit.value)}
        />
      ))}
    </DetailsLayout>
  );
};

export { ResultsSidebarDetailsRespondents, ResultsSidebarDetailsAudience, DetailsItem, DetailsTitle, DetailsLayout };
